import { handleCancelEditionPanel } from '@features/divisions/containers/SingleDivisionContainer/SingleDivisionContainer.handlers';
import { FormType } from '@features/divisions/containers/SingleDivisionContainer/SingleDivisionContainer.types';
import { DivisionEditionFormType } from '@features/divisions/interfaces/division-edition.interface';
import {
	PROCESS_NAME_FORMAT,
	PROCESS_NUMBER_FORMAT,
	TOPIC_PUSH_FORMAT,
} from '@features/processes/constants/process-forms.constants';
import ProcessCreationFormType from '@features/processes/interfaces/process-creation.interface';
import { ProcessEditionFormType } from '@features/processes/interfaces/process-edition.interface';
import { DivisionCliente, ProcesoDivision } from '@interfaces/firestore-documents.interfaces';
import { RequestFn } from '@interfaces/requests.interfaces';
import { OptionsObject, SnackbarKey, SnackbarMessage } from 'notistack';
import {
	ChangeEventHandler,
	ClipboardEventHandler,
	Dispatch,
	KeyboardEventHandler,
	SetStateAction,
} from 'react';
import { UseFormReturn } from 'react-hook-form';

export function handleProcessNameChange(
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onChange: (...event: any[]) => void
): ChangeEventHandler<HTMLInputElement> {
	return function (e) {
		const { value } = e.target;

		// Validamos si el texto tiene un formato válido.
		const isValidText = value && value.length > 0;

		// Colocamos la primera letra del nombre en mayúscula.
		onChange(isValidText ? value[0].toUpperCase() + value.slice(1, 100) : '');
	};
}

export const handleProcessNameKeyPress: KeyboardEventHandler<HTMLInputElement> = (e) => {
	if (!PROCESS_NAME_FORMAT.test(e.key) && e.key !== 'Backspace') {
		e.preventDefault();
	}
};

export const handleProcessNamePaste: ClipboardEventHandler<HTMLInputElement> = (e) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const textToCopy = (e.clipboardData || (window as any).clipboardData).getData('text');

	// Validamos si el texto tiene un formato válido.
	const isValidText = textToCopy && textToCopy.length > 0 && PROCESS_NAME_FORMAT.test(textToCopy);
	if (!isValidText) {
		e.preventDefault();
	}
};

export const handleProcessNumberKeyPress: KeyboardEventHandler<HTMLInputElement> = (e) => {
	if (!PROCESS_NUMBER_FORMAT.test(e.key) && e.key !== 'Backspace') {
		e.preventDefault();
	}
};

export const handleProcessNumberPaste: ClipboardEventHandler<HTMLInputElement> = (e) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const textToCopy = (e.clipboardData || (window as any).clipboardData).getData('text');

	// Validamos si el texto tiene un formato válido.
	const isValidText =
		textToCopy && textToCopy.length > 0 && PROCESS_NUMBER_FORMAT.test(textToCopy);

	if (!isValidText) {
		e.preventDefault();
	}
};

export function handleTopicPushChange(
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onChange: (...event: any[]) => void
): ChangeEventHandler<HTMLInputElement> {
	return function (e) {
		const { value } = e.target;

		// Validamos si el texto tiene un formato válido.
		const isValidText = value && value.length > 0 && TOPIC_PUSH_FORMAT.test(value);

		onChange(isValidText ? value.slice(0, 50) : '');
	};
}

export const handleTopicPushKeyPress: KeyboardEventHandler<HTMLInputElement> = (e) => {
	if (!TOPIC_PUSH_FORMAT.test(e.key) && e.key !== 'Backspace') {
		e.preventDefault();
	}
};

export const handleTopicPushPaste: ClipboardEventHandler<HTMLInputElement> = (e) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const textToCopy = (e.clipboardData || (window as any).clipboardData).getData('text');

	// Validamos si el texto tiene un formato válido.
	const isValidText = textToCopy && textToCopy.length > 0 && TOPIC_PUSH_FORMAT.test(textToCopy);
	if (!isValidText) {
		e.preventDefault();
	}
};

export function handleAddGuideRangeForm(
	state: boolean,
	setIsAddGuidesRangeFormOpen: Dispatch<SetStateAction<boolean>>
): () => void {
	return function () {
		setIsAddGuidesRangeFormOpen(state);
	};
}

export function handleSaveProcessData(
	afterSaveOnBeforeCloseDialogAction: (() => void) | null,
	defaultData: ProcesoDivision,
	divisionData: DivisionCliente,
	divisionEditionForm: UseFormReturn<DivisionEditionFormType>,
	enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey,
	newData: ProcessEditionFormType,
	nit: string,
	processCreationForm: UseFormReturn<ProcessCreationFormType>,
	processEditionForm: UseFormReturn<ProcessEditionFormType>,
	request: RequestFn,
	requestBackendSuite: RequestFn,
	setAfterSaveOnBeforeCloseDialogAction: Dispatch<SetStateAction<(() => void) | null>>,
	setBeforeCloseDialog: Dispatch<SetStateAction<boolean>>,
	setBeforeSaveData: Dispatch<SetStateAction<ProcessEditionFormType | null>>,
	setBeforeSaveDialog: Dispatch<SetStateAction<string>>,
	setEditionProcessData: Dispatch<SetStateAction<ProcesoDivision | null>>,
	setForm: Dispatch<SetStateAction<FormType>>,
	setShow: React.Dispatch<React.SetStateAction<boolean>>,
	getDataById: (identificacion: string) => Promise<void>
): () => Promise<void> {
	return async function () {
		try {
			setShow(true);
			// Comprobamos si se cambió el consecutivo desde o el consecutivo hasta.
			if (
				defaultData.consecutivo_desde != newData.consecutivo_desde ||
				defaultData.consecutivo_hasta != newData.consecutivo_hasta
			) {
				const response = await requestBackendSuite('procesos/rangos', 'PUT', {
					id_proceso: defaultData.id_proceso,
					asignacion: {
						consecutivo_desde: newData.consecutivo_desde,
						consecutivo_hasta: newData.consecutivo_hasta,
					},
				});

				if (response.isError) {
					throw new Error(response.message);
				}
			}

			// Si el cliente no genera la numeración y el consecutivo actual se cambió, se actualiza.
			if (!newData.cliente_genera_numeracion) {
				if (defaultData.consecutivo_actual != newData.consecutivo_actual) {
					const response = await requestBackendSuite('procesos/rangos', 'PUT', {
						id_proceso: defaultData.id_proceso,
						asignacion: {
							consecutivo_actual: newData.consecutivo_actual,
							consecutivo_desde: newData.consecutivo_desde,
							consecutivo_hasta: newData.consecutivo_hasta,
						},
					});

					if (response.isError) {
						throw new Error(response.message);
					}
				}
			}

			// TODO: Actualizar los demás rangos de guías que se editen (newData.ranges).

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const dataToSave: { [key: string]: any } = {};

			// Comprobamos si se cambió el nombre del proceso.
			if (
				defaultData.nombre_proceso !== newData.nombre_proceso &&
				newData.nombre_proceso.length > 0
			) {
				dataToSave.nombre_proceso = newData.nombre_proceso;
			}

			// Comprobamos si se cambió el estado de la liquidación en guía.
			if (defaultData.liquidacion_en_guia !== newData.liquidacion_en_guia) {
				dataToSave.liquidacion_en_guia = newData.liquidacion_en_guia;
			}

			// Comprobamos si se cambió el estado de la liquidación en despacho.
			if (defaultData.liquidacion_en_despacho !== newData.liquidacion_en_despacho) {
				dataToSave.liquidacion_en_despacho = newData.liquidacion_en_despacho;
			}

			// Comprobamos si se cambió el estado de la notificación a destinatario.
			if (defaultData.notificacion_destinatario !== newData.notificacion_destinatario) {
				dataToSave.notificacion_destinatario = newData.notificacion_destinatario;
			}

			// Comprobamos si se cambió el estado del cotizador.
			if (defaultData.cotizador !== newData.cotizador) {
				dataToSave.cotizador = newData.cotizador;
			}

			// Comprobamos si se cambió el cliente genera numeración.
			if (defaultData.cliente_genera_numeracion !== newData.cliente_genera_numeracion) {
				dataToSave.cliente_genera_numeracion = newData.cliente_genera_numeracion;
			}

			if (defaultData.flag_referencia_unica !== newData.flag_referencia_unica) {
				dataToSave.flag_referencia_unica = newData.flag_referencia_unica;
			}

			// Comprobamos si se cambió el topic_push.
			const defaultTopicPush = (defaultData.topic_push ?? '').toLowerCase();
			const newTopicPush = (newData.topic_push ?? '').toLowerCase();
			if (defaultTopicPush !== newTopicPush) {
				dataToSave.topic_push = newData.topic_push ?? '';
			}

			// Si se ha editado alguno de los campos anteriores se guardan.
			if (Object.keys(dataToSave).length > 0) {
				const response = await requestBackendSuite('procesos', 'PUT', {
					id_proceso: defaultData.id_proceso,
					proceso: dataToSave,
				});

				if (response.isError) {
					throw new Error(response.message);
				}
			}

			// Comprobamos si se cambió el estado del proceso.
			if (defaultData.estado !== newData.estado) {
				const response = await requestBackendSuite('procesos/estado', 'PUT', {
					id_proceso: defaultData.id_proceso,
					estado_proceso: newData.estado,
				});

				if (response.isError) {
					throw new Error(response.message);
				}
			}
			const newRanges = newData.ranges.filter((newRange) => {
				const defaultRange = defaultData.ranges.find(
					(p) => p.id_rango === newRange.id_rango
				);
				if (!defaultRange) {
					return true;
				}
				return (
					defaultRange.consecutivo_desde != newRange.consecutivo_desde ||
					defaultRange.consecutivo_hasta != newRange.consecutivo_hasta ||
					defaultRange.consecutivo_actual != newRange.consecutivo_actual ||
					defaultRange.en_uso !== newRange.en_uso ||
					defaultRange.nombre !== newRange.nombre ||
					defaultRange.direccion !== newRange.direccion ||
					defaultRange.telefono !== newRange.telefono ||
					defaultRange.ciudad !== newRange.ciudad ||
					defaultRange.nombre_ciudad !== newRange.nombre_ciudad ||
					defaultRange.correo !== newRange.correo
				);
			});
			if (newRanges.length > 0) {
				for (const newRange of newRanges) {
					const response = await request(
						'clientes/suite-maestro-rangos/rango-maestro',
						'PUT',
						{
							id_rango: newRange.id_rango,
							rango: {
								consecutivo_actual: newRange.consecutivo_actual,
								consecutivo_desde: newRange.consecutivo_desde,
								consecutivo_hasta: newRange.consecutivo_hasta,
								en_uso: newRange.en_uso,
							},
							detalle_rango: {
								nombre: newRange.nombre,
								direccion: newRange.direccion,
								telefono: newRange.telefono,
								ciudad: newRange.ciudad,
								nombre_ciudad: newRange.nombre_ciudad,
								correo: newRange.correo,
							},
						}
					);
					if (response.isError) {
						throw new Error(response.message);
					}
				}
			}
			// Comprobamos si se cambiaron los niveles de servicio asociados al proceso.
			const defaultServices = defaultData.servicios_asociados.sort((a, b) => a - b);
			const newServices = newData.servicios_asociados.map(Number).sort((a, b) => a - b);
			const servicesToAdd = newServices.filter((x) => !defaultServices.includes(x));
			if (servicesToAdd.length > 0) {
				const response = await requestBackendSuite('procesos/niveles-servicio', 'PUT', {
					codigo_div: divisionData.codigo_div,
					id_proceso: defaultData.id_proceso,
					identificacion: nit,
					niveles_servicio: servicesToAdd,
				});

				if (response.isError) {
					throw new Error(response.message);
				}
			}

			const servicesToRemove = defaultServices.filter((x) => !newServices.includes(x));
			if (servicesToRemove.length > 0) {
				const response = await requestBackendSuite('procesos/niveles-servicio', 'DELETE', {
					codigo_div: divisionData.codigo_div,
					id_proceso: defaultData.id_proceso,
					identificacion: nit,
					niveles_servicio: servicesToRemove,
				});

				if (response.isError) {
					throw new Error(response.message);
				}
			}

			// Comprobamos si se cambiaron los combos asociados al proceso.
			const defaultCombos = defaultData.combos_asociados.sort((a, b) => a - b);
			const newCombos = newData.combos_asociados.map(Number).sort((a, b) => a - b);

			const combosToAdd = newCombos.filter((x) => !defaultCombos.includes(x));
			if (combosToAdd.length > 0) {
				const response = await requestBackendSuite('procesos/combos', 'PUT', {
					codigo_div: divisionData.codigo_div,
					combos: combosToAdd,
					identificacion: nit,
					id_proceso: defaultData.id_proceso,
				});

				if (response.isError) {
					throw new Error(response.message);
				}
			}

			const combosToRemove = defaultCombos.filter((x) => !newCombos.includes(x));
			if (combosToRemove.length > 0) {
				const response = await requestBackendSuite('procesos/combos', 'DELETE', {
					codigo_div: divisionData.codigo_div,
					combos: combosToRemove,
					id_proceso: defaultData.id_proceso,
					identificacion: nit,
				});

				if (response.isError) {
					throw new Error(response.message);
				}
			}

			handleCancelEditionPanel(
				afterSaveOnBeforeCloseDialogAction,
				divisionData,
				divisionEditionForm,
				processCreationForm,
				processEditionForm,
				setAfterSaveOnBeforeCloseDialogAction,
				setBeforeCloseDialog,
				setBeforeSaveData,
				setBeforeSaveDialog,
				setEditionProcessData,
				setForm
			)();
			await getDataById(nit);
			setShow(false);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setShow(false);
			setBeforeSaveData(null);
			setBeforeSaveDialog('');

			if (typeof error === 'string') {
				enqueueSnackbar(error, { variant: 'error' });
			} else if (error?.message) {
				enqueueSnackbar(error.message, { variant: 'error' });
			} else if (error.response && error.response.data.message) {
				enqueueSnackbar(error.response.data.message, { variant: 'error' });
			} else {
				console.error('Error al guardar proceso', error);
				enqueueSnackbar('No se pudieron guardar los cambios', { variant: 'error' });
			}
		}
	};
}

export function handleSaveProcessEditionData(
	afterSaveOnBeforeCloseDialogAction: (() => void) | null,
	defaultData: ProcesoDivision | null,
	divisionData: DivisionCliente,
	divisionEditionForm: UseFormReturn<DivisionEditionFormType>,
	enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey,
	nit: string,
	processCreationForm: UseFormReturn<ProcessCreationFormType>,
	processEditionForm: UseFormReturn<ProcessEditionFormType>,
	request: RequestFn,
	requestBackendSuite: RequestFn,
	setAfterSaveOnBeforeCloseDialogAction: Dispatch<SetStateAction<(() => void) | null>>,
	setBeforeCloseDialog: Dispatch<SetStateAction<boolean>>,
	setBeforeSaveData: Dispatch<SetStateAction<ProcessEditionFormType | null>>,
	setBeforeSaveDialog: Dispatch<SetStateAction<string>>,
	setEditionProcessData: Dispatch<SetStateAction<ProcesoDivision | null>>,
	setForm: Dispatch<SetStateAction<FormType>>,
	setShow: React.Dispatch<React.SetStateAction<boolean>>,
	getDataById: (identificacion: string) => Promise<void>
): () => void {
	return processEditionForm.handleSubmit(async (newData) => {
		if (!defaultData) return;

		let beforeSaveDialog = '';
		if (defaultData.prefijo_consecutivo != newData.prefijo_consecutivo) {
			beforeSaveDialog = 'campo Prefijo';
		}

		if (defaultData.consecutivo_desde != newData.consecutivo_desde) {
			beforeSaveDialog = beforeSaveDialog.length > 0 ? 'all' : 'campo Inicial';
		}

		if (defaultData.consecutivo_hasta != newData.consecutivo_hasta) {
			beforeSaveDialog = beforeSaveDialog.length > 0 ? 'all' : 'campo Final';
		}

		if (defaultData.consecutivo_actual != newData.consecutivo_actual) {
			beforeSaveDialog = beforeSaveDialog.length > 0 ? 'all' : 'campo Actual';
		}

		if (beforeSaveDialog.length === 0 && newData.ranges.length > 0) {
			for (const newRange of newData.ranges) {
				const lastRange = defaultData.ranges.find((p) => p.id_rango === newRange.id_rango);
				if (lastRange) {
					if (lastRange.prefijo_consecutivo != newRange.prefijo_consecutivo) {
						beforeSaveDialog = beforeSaveDialog.length > 0 ? 'all' : 'campo Prefijo';
					}

					if (lastRange.consecutivo_desde != newRange.consecutivo_desde) {
						beforeSaveDialog = beforeSaveDialog.length > 0 ? 'all' : 'campo Inicial';
					}

					if (lastRange.consecutivo_hasta != newRange.consecutivo_hasta) {
						beforeSaveDialog = beforeSaveDialog.length > 0 ? 'all' : 'campo Final';
					}

					if (lastRange.consecutivo_actual != newRange.consecutivo_actual) {
						beforeSaveDialog = beforeSaveDialog.length > 0 ? 'all' : 'campo Actual';
					}
				}
			}
		}

		if (beforeSaveDialog.length > 0) {
			setBeforeSaveData(newData);
			setBeforeSaveDialog(beforeSaveDialog);
			return;
		}

		await handleSaveProcessData(
			afterSaveOnBeforeCloseDialogAction,
			defaultData,
			divisionData,
			divisionEditionForm,
			enqueueSnackbar,
			newData,
			nit,
			processCreationForm,
			processEditionForm,
			request,
			requestBackendSuite,
			setAfterSaveOnBeforeCloseDialogAction,
			setBeforeCloseDialog,
			setBeforeSaveData,
			setBeforeSaveDialog,
			setEditionProcessData,
			setForm,
			setShow,
			getDataById
		)();
	});
}
